/**
 * @module builder
 */
import identity from 'lodash/identity'
import sortBy from 'lodash/sortBy'
import type { OptionsDocument, PresentationBlockOption, PresentationSectionOption, QuoteFile } from 'paintscout'
import { getObjectLabels } from '../../../util/get-object-labels'
import { getFeature } from '../../get-features'
import { uuid } from '../../../util/uuid'

export const v2PlaceholderImage: QuoteFile = {
  src: 'https://paintscout.s3.us-east-1.amazonaws.com/presentations/paint-exterior.png',
  format: 'png',
  s3PublicKey: 'presentations/paint-exterior',
  cloudinaryPublicId: 'placeholders/image',
  width: 1536,
  height: 1024,
  type: 'upload',
  visibility: 'visible'
}

const placeholderImage: QuoteFile = {
  src: 'https://res.cloudinary.com/taptapquote/image/upload/v1581797484/placeholders/image.png',
  format: 'png',
  cloudinaryPublicId: 'placeholders/image',
  width: 100,
  height: 100,
  type: 'upload',
  visibility: 'visible'
}

export function getPresentationSectionOptions(args: {
  options?: OptionsDocument
  type?: string
  skipSort?: boolean
  v2?: boolean
}): PresentationSectionOption[] {
  const { type, skipSort = false, options, v2 = false } = args
  const objectLabels = getObjectLabels({ options })
  const hasAboutYourEstimator = !v2 && getFeature({ options, path: 'presentation.aboutYourEstimator' })

  let sectionOptions = [
    // about-estimator: always in v2, feature-flagged in v1
    (v2 || hasAboutYourEstimator) && {
      type: 'about-estimator',
      label: 'About Your Estimator',
      title: 'About Your Estimator',
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: true
      }
    },

    {
      type: 'cover',
      label: 'Cover Page',
      title: 'Cover',
      background: { noPadding: false, color: '#fff' },
      fields: {
        subtitle: 'Estimate\nDescription',
        alignment: 'justify',
        verticalAlign: 'middle',
        backgroundOpacity: 1,
        overlayImage: placeholderImage
      },
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },

    // button: v2-only
    v2 && {
      type: 'button',
      label: 'Button',
      title: 'Button',
      background: { noPadding: false, color: '' },
      fields: {
        animation: 'none',
        label: 'New Button',
        href: '',
        color: '',
        horizontalAlignInContainer: 'center'
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: 'custom',
      label: 'Custom HTML/CSS',
      title: 'New Custom Section',
      fields: {
        html: '<blockquote class="blockquote">Pellentesque habitant morbi tristique senectus<br />et netus et malesuada fames ac turpis egestas. Vestibulum tortor quam, <br />feugiat vitae, ultricies eget, tempor sit amet, ante.</blockquote><pre><code>Morbi in\nsem quis\ndui placerat ornare.\nPellentesque odio nisi, \neuismod in</code></pre>',
        css: '.blockquote {border-left: 5px solid #ddd; margin-left: 0; padding-left: 20px;margin-bottom:20px;} pre {background: #ddd; padding: 20px; color: #000; display: inline-block; border-radius: 3px;}'
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // social-links: v2-only (replaces socials in v1)
    v2 && {
      type: 'social-links',
      label: 'Social Links',
      title: 'Social Links',
      background: { noPadding: false, color: '' },
      fields: {
        links: [
          { label: 'Facebook', href: 'https://example.com' },
          { label: 'Instagram', href: 'https://example.com' },
          { label: 'LinkedIn', href: '' },
          { label: 'Pinterest', href: '' },
          { label: 'Twitter', href: '' },
          { label: 'YouTube', href: '' },
          { label: 'Custom', href: '' }
        ],
        fontSize: '3rem',
        alignment: 'center'
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: 'divider',
      label: 'Divider',
      title: 'Divider',
      background: { noPadding: v2, color: v2 ? '' : '#fff' },
      fields: {
        marginTop: 40,
        marginBottom: 40,
        marginTopResponsive: 'md',
        marginBottomResponsive: 'md'
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: 'documents',
      label: 'Documents',
      title: 'Important Documents',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        rows: [
          {
            title: 'Document Title',
            content:
              '<p>Describe the details and/or the importance of the associated document in the box provided here.</p>',
            image: {
              src: '',
              format: 'pdf',
              cloudinaryPublicId: '',
              width: 612,
              height: 792,
              type: 'upload',
              visibility: 'visible'
            }
          }
        ]
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: 'image',
      label: v2 ? 'Media' : 'Image',
      title: '',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        image: null,
        placeholder: ''
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // video: v2-only
    v2 && {
      type: 'video',
      label: 'Video',
      title: '',
      background: { noPadding: false, color: '' },
      fields: {
        video: null
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // cards: v2-only
    v2 && {
      type: 'cards',
      label: 'Cards',
      title: '',
      background: { noPadding: false, color: '' },
      fields: {
        animation: 'none',
        columns: 3,
        cards: [
          {
            file: { ...v2PlaceholderImage, presentationOptions: { aspectRatio: '3/2' } },
            content: `<h3><span>Quisque aliquet</span></h3><p></p><p><span>Quisque et vehicula velit. Nam blandit sem in nisi pretium, ut bibendum ipsum ultrices.</span></p>`,
            key: uuid()
          },
          {
            file: { ...v2PlaceholderImage, presentationOptions: { aspectRatio: '3/2' } },
            content: `<h3><span>Morbi sollicitudin</span></h3><p></p><p><span>Nibh eget libero cursus, quis fermentum neque laoreet. Vestibulum et massa volutpat, rhoncus ipsum et, placerat est.</span></p>`,
            key: uuid()
          },
          {
            file: { ...v2PlaceholderImage, presentationOptions: { aspectRatio: '3/2' } },
            content: `<h3><span>Mauris efficitur massa ac</span></h3><p></p><p><span>Vivamus neque magna, elementum a sollicitudin eget, dictum at enim.</span></p>`,
            key: uuid()
          }
        ],
        useCarousel: false
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // sliderImage: v1-only
    !v2 && {
      type: 'sliderImage',
      label: 'Slider Image',
      title: '',
      background: { noPadding: false, color: '#fff' },
      fields: {
        leftImage: null,
        rightImage: null,
        placeholder: ''
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // slider-image: v2-only (native port of v1's sliderImage, #21821 — kebab-case key to match
    // other v2-only types; v1's own camelCase entry above is untouched)
    v2 && {
      type: 'slider-image',
      label: 'Slider Image',
      title: '',
      background: { noPadding: false, color: '#fff' },
      fields: {
        leftImage: null,
        rightImage: null,
        // starts flush (no vertical padding) rather than the 'sm' default (#21821)
        sectionHeight: 'x-sm'
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: 'gallery',
      label: 'Gallery',
      title: '',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        columns: 3,
        showBorder: false,
        ...(v2 && {
          files: [
            { ...v2PlaceholderImage, presentationOptions: { aspectRatio: '3/2' } },
            { ...v2PlaceholderImage, presentationOptions: { aspectRatio: '3/2' } },
            { ...v2PlaceholderImage, presentationOptions: { aspectRatio: '3/2' } }
          ]
        })
      },
      active: true,
      advanced: {
        useForNav: !v2,
        allowCustom: false
      }
    },

    {
      type: 'awards',
      label: 'Images Row',
      // v2 shows the type in the sidebar tree, so a title echoing the label is pure noise as an h2
      title: v2 ? '' : 'Images Row',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        alignment: 'middle',
        rows: [
          {
            image: {
              src: 'https://res.cloudinary.com/taptapquote/image/upload/v1581797484/placeholders/award-placeholder.png',
              format: 'png',
              cloudinaryPublicId: 'placeholders/award-placeholder',
              width: 272,
              height: 326,
              type: 'upload',
              visibility: 'visible'
            }
          },
          {
            image: {
              src: 'https://res.cloudinary.com/taptapquote/image/upload/v1581797484/placeholders/award-placeholder.png',
              format: 'png',
              cloudinaryPublicId: 'placeholders/award-placeholder',
              width: 272,
              height: 326,
              type: 'upload',
              visibility: 'visible'
            }
          },
          {
            image: {
              src: 'https://res.cloudinary.com/taptapquote/image/upload/v1581797484/placeholders/award-placeholder.png',
              format: 'png',
              cloudinaryPublicId: 'placeholders/award-placeholder',
              width: 272,
              height: 326,
              type: 'upload',
              visibility: 'visible'
            }
          }
        ]
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: 'introduction',
      label: 'Introduction',
      title: '',
      background: { noPadding: false, color: '#fff' },
      fields: {
        image: null as any,
        content: ''
      },
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: true
      }
    },

    {
      type: 'mission',
      label: 'Centered Text',
      // v2 shows the type in the sidebar tree, so a title echoing the label is pure noise as an h2
      title: v2 ? '' : 'Centered Text',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        content:
          '<p>Nulla ornare nulla luctus magna elementum, et ullamcorper odio feugiat.</p><p><strong>Maecenas efficitur quis massa ac pretium.</strong></p>',
        image: placeholderImage
      },
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },

    // pictures: not offered in the v2 add-block catalog (#21821) — it stays supported as a
    // render target + converter output for v1 sections carried over, but isn't a fresh
    // v2 block while product decides on the catalog
    !v2 && {
      type: 'pictures',
      label: `${objectLabels.quote.value} Pictures`,
      title: `${objectLabels.quote.value} Pictures`,
      fields: {},
      background: {
        style: 'contained',
        color: '#fff'
      },
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },

    {
      type: 'quote',
      label: objectLabels.quote.value,
      title: objectLabels.quote.value,
      fields: {
        asCartLink: false,
        label: `View ${objectLabels.quote.value}`,
        color: '',
        buttonSize: 'sm',
        horizontalAlignInContainer: 'center'
      },
      background: {
        style: 'contained',
        color: v2 ? '' : '#fff'
      },
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },

    {
      type: 'services',
      label: 'Two Column Rows',
      // v2 shows the type in the sidebar tree, so a title echoing the label is pure noise as an h2
      title: v2 ? '' : 'Two Column Rows',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        rows: [
          {
            title: 'Title',
            content:
              '<p>Fusce eget ipsum felis. Aenean elit leo, interdum at aliquam ac, blandit quis tellus. Sed accumsan urna in pretium dignissim. Suspendisse imperdiet libero vel purus mattis auctor. Morbi imperdiet enim ac nulla pulvinar, vitae tempus eros fermentum. Duis id neque diam.</p>',
            image: placeholderImage
          }
        ]
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: 'terms',
      label: 'Terms',
      title: 'Terms',
      useForNav: true,
      allowCustom: false,
      active: true,
      fields: {},
      background: {
        style: 'contained',
        color: v2 ? '' : '#fff'
      },
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },

    // testimonials: different structure between v1 and v2
    v2
      ? {
          type: 'testimonials',
          label: 'Testimonials',
          title: '',
          background: { noPadding: false, color: '', useTheme: true },
          fields: {
            animation: 'none',
            columns: 3,
            cards: [
              {
                content:
                  '<p>Add testimonials from your clients that underscore the quality your company brings. Research by Dixa showed that <strong>97%</strong> of participants factor customer reviews into their buying decisions. The same study shows that customers are <strong>18%</strong> more likely to buy from a website with customer reviews over on that does not!</p>',
                credit: '<h3>Jessie, company inc.</h3>',
                key: uuid()
              },
              {
                content:
                  '<p>The same study shows that customers are <strong>18%</strong> more likely to buy from a website with customer reviews over on that does not!</p>',
                credit: '<h3>Carey B.</h3>',
                key: uuid()
              },
              {
                content:
                  '<p>Research by Dixa showed that <strong>97%</strong> of participants factor customer reviews into their buying decisions. The same study shows that customers are <strong>18%</strong> more likely to buy from a website with customer reviews over on that does not!</p>',
                credit: '<h3>Taylor C.</h3>',
                key: uuid()
              }
            ],
            sectionHeight: 'md'
          },
          active: true,
          advanced: {
            useForNav: false,
            allowCustom: false
          }
        }
      : {
          type: 'testimonials',
          label: 'Testimonials',
          title: 'Testimonials',
          background: { noPadding: false, color: '#fff' },
          fields: {
            rows: [
              {
                content: '<p>Nullam eget magna non sapien fermentum maximus id in erat.</p>',
                credit: 'Bob Johnson'
              },
              {
                content: '<p>Sed gravida magna sed sapien efficitur rhoncus.</p>',
                credit: 'Steve Williams'
              },
              {
                content: '<p>Nulla euismod tempor vehicula.</p>',
                credit: 'Gary Jones'
              }
            ]
          },
          active: true,
          advanced: {
            useForNav: false,
            allowCustom: false
          }
        },

    // socials: v1-only (v2 uses social-links instead)
    !v2 && {
      type: 'socials',
      label: 'Socials',
      title: 'Socials',
      background: { noPadding: false, color: '#fff' },
      fields: {
        rows: [
          { link: 'https://www.instagram.com', icon: 'instagram' },
          { link: 'https://www.facebook.com', icon: 'facebook' },
          { link: 'https://www.youtube.com', icon: 'youtube' }
        ]
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // text (SB-style): v2-only
    v2 && {
      type: 'text',
      label: 'Text',
      title: 'New Text',
      background: { noPadding: false, color: '' },
      fields: {
        content: '<h2>Etiam ante sapien, pharetra blandit commodo ut, malesuada vel augue.</h2>',
        color: ''
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // banner: v2-only
    v2 && {
      type: 'banner',
      label: 'Banner',
      title: 'Banner',
      background: { noPadding: false, color: '' },
      fields: {
        animation: 'none',
        content: `<h1 style="text-align: center">Lorem Ipsum</h1><p style="text-align: center"></p><h2 style="text-align: center">Fusce ut odio suscipit dolor elementum ultrices at vel quam</h2>`,
        fullWidthImage: false,
        image: v2PlaceholderImage,
        layout: 'poster',
        layoutOptions: {
          poster: { color: '#ffffff' },
          collage: {
            color: '',
            textBackgroundColor: '',
            useGlassEffect: false,
            verticalAlign: 'start',
            imagePosition: 'left'
          }
        }
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // logo: v2-only
    v2 && {
      type: 'logo',
      label: 'Logo',
      title: 'Logo',
      background: { noPadding: false, color: '' },
      fields: {
        animation: 'none',
        color: '#ffffff',
        content: '',
        contentBackgroundColor: '',
        contentColor: '',
        contentTop: '',
        image: v2PlaceholderImage,
        logoAlignment: 'center',
        logoSize: 'md',
        useContentBackground: false
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    // spacer: v2-only
    v2 && {
      type: 'spacer',
      label: 'Spacer',
      title: 'Spacer',
      background: { noPadding: true, color: '' },
      fields: {
        heightResponsive: 'md'
      },
      active: true,
      advanced: {
        useForNav: false,
        allowCustom: false
      }
    },

    {
      type: '2-column',
      label: '2 Column',
      // v1 renders this as the section's h2 (and it was the placeholder "My New Section"); the v2
      // block has no heading at all, so it starts blank
      title: v2 ? '' : 'My New Section',
      background: { noPadding: false, color: v2 ? '' : '#fff', useTheme: true },
      fields: {
        animation: 'none',
        columnAlign: 'center',
        fullWidthImage: true,
        verticalAlign: v2 ? 'center' : 'middle',
        leftType: 'image',
        leftOptions: { color: '', backgroundColor: '' },
        leftImage: v2 ? null : placeholderImage,
        leftWidth: 6,
        rightType: 'text',
        rightOptions: { color: '', backgroundColor: '' },
        rightText:
          '<p>Morbi ultrices, augue ut fermentum euismod, orci dui dignissim libero, ac mollis risus sem in nisi.</p><p></p><p>Sed cursus nibh vel placerat dapibus. Maecenas semper vestibulum congue. Nulla euismod tempor vehicula.</p><p></p><p>Phasellus efficitur turpis tempus, viverra velit ut, fringilla magna.</p>',
        rightWidth: 6
      },
      active: true,
      advanced: {
        useForNav: !v2,
        allowCustom: false
      }
    },

    {
      type: 'text',
      label: 'Text Block',
      title: 'New Text Section',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        content:
          '<p>Morbi ultrices, augue ut fermentum euismod, orci dui dignissim libero, ac mollis risus sem in nisi.</p><p></p><p>Sed cursus nibh vel placerat dapibus. Maecenas semper vestibulum congue. Nulla euismod tempor vehicula.</p><p></p><p>Phasellus efficitur turpis tempus, viverra velit ut, fringilla magna.</p>'
      },
      active: true,
      advanced: {
        useForNav: !v2,
        allowCustom: false
      }
    },

    {
      type: 'work-order',
      label: objectLabels.workOrder.value,
      title: objectLabels.workOrder.value,
      fields: {},
      background: {
        style: 'contained',
        color: v2 ? '' : '#fff'
      },
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },

    {
      type: '3-column',
      label: '3 Column',
      // v1 renders this as the section's h2 (and it was the placeholder "My New Section"); the v2
      // block has no heading at all, so it starts blank
      title: v2 ? '' : 'My New Section',
      background: { noPadding: false, color: v2 ? '' : '#fff' },
      fields: {
        animation: 'none',
        columnAlign: 'center',
        verticalAlign: v2 ? 'center' : 'middle',
        leftType: 'image',
        leftOptions: { color: '', backgroundColor: '' },
        leftImage: v2 ? null : placeholderImage,
        centerType: 'text',
        centerOptions: { color: '', backgroundColor: '' },
        centerText:
          '<p>Morbi ultrices, augue ut fermentum euismod, orci dui dignissim libero, ac mollis risus sem in nisi.</p><p></p><p>Sed cursus nibh vel placerat dapibus. Maecenas semper vestibulum congue. Nulla euismod tempor vehicula.</p>',
        rightType: 'image',
        rightOptions: { color: '', backgroundColor: '' },
        rightImage: v2 ? null : placeholderImage
      },
      active: true,
      advanced: {
        useForNav: !v2,
        allowCustom: false
      }
    },

    // sherwin-williams / benjamin-moore: v1-only
    !v2 && {
      type: 'sherwin-williams-popular-colors',
      label: 'Most Popular Colors',
      title: 'Most Popular Colors',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'sherwin-williams-color-of-the-year',
      label: 'Color of the Year',
      title: 'Color of the Year',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'sherwin-williams-top-colors',
      label: 'Top Colors',
      title: 'Top Colors'
    },
    !v2 && {
      type: 'sherwin-williams-finest-whites-neutrals',
      label: 'Brightest Whites & Neutrals',
      title: 'Brightest Whites & Neutrals',
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },
    {
      type: 'sherwin-williams-designer-color-collection',
      label: 'Designer Color Collection',
      title: 'Designer Color Collection',
      active: true,
      advanced: {
        useForNav: true,
        allowCustom: false
      }
    },
    {
      type: 'sherwin-williams-colormix-forecast',
      label: 'Colormix Forecast',
      title: 'Colormix Forecast',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'sherwin-williams-explore-exterior',
      label: 'Explore Exterior Paint',
      title: 'Explore Exterior Paint',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'sherwin-williams-schedule-consultation',
      label: 'Schedule Free Consultation',
      title: 'Schedule Free Consultation',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'sherwin-williams-paint-color-visualizer',
      label: 'Paint Color Visualizer',
      title: 'Paint Color Visualizer',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'sherwin-williams-sample-colors',
      label: 'Sample Color In Your Space',
      title: 'Sample Color In Your Space',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'sherwin-williams-color-expert',
      label: 'Color Expert App',
      title: 'Color Expert App',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    },
    !v2 && {
      type: 'benjamin-moore-main',
      label: 'Benjamin Moore Main Page',
      title: 'Benjamin Moore Main Page',
      active: true,
      advanced: { useForNav: true, allowCustom: false }
    }
  ].filter(identity)

  if (!skipSort) {
    sectionOptions = sortBy(sectionOptions, ['label'])
  }

  if (type) {
    if (!v2) {
      if (type === 'default') {
        return sectionOptions.filter(
          (option) => !option.type.startsWith('sherwin-williams') && !option.type.startsWith('benjamin-moore')
        )
      } else if (type.includes('sherwin-williams')) {
        return sectionOptions.filter((option) => option.type.startsWith('sherwin-williams'))
      } else if (type.includes('benjamin-moore')) {
        return sectionOptions.filter((option) => option.type.startsWith('benjamin-moore'))
      }
    }
    return sectionOptions.filter((option) => option.type === type)
  }

  return sectionOptions
}

/**
 * The v2 **block** catalog — the addable content types (text, image, gallery, the estimate, …).
 *
 * Deliberately a thin wrapper rather than a copy: this catalog is a list of content *types*, and
 * the same ~24 types are v1 sections and v2 blocks. Only the level's name differs, so duplicating
 * ~800 lines of type definitions to rename them would guarantee the two drift apart. Use this from
 * v2 code so callers read in the right vocabulary and get PresentationBlockOption back.
 */
export function getPresentationBlockOptions(args: {
  options?: OptionsDocument
  type?: string
  skipSort?: boolean
}): PresentationBlockOption[] {
  return getPresentationSectionOptions({ ...args, v2: true })
}
